import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import MissingClerkKey from "./pages/MissingClerkKey";
import { registerServiceWorker } from "./lib/pwa";
import "./index.css";

// Vite only exposes vars prefixed with VITE_ to client code. We accept either
// VITE_CLERK_PUBLISHABLE_KEY (canonical) or VITE_CLERK_KEY (short alias) so a
// typo on either name doesn't blank the app.
const env = import.meta.env as Record<string, string | undefined>;
const publishableKey = env.VITE_CLERK_PUBLISHABLE_KEY ?? env.VITE_CLERK_KEY;

// Clerk publishable keys are pk_test_<base64> or pk_live_<base64> where the
// suffix base64-decodes to the frontend-API host. Passing a placeholder string
// (e.g. "pk_test_replace_me") makes ClerkProvider throw on mount, which kills
// the React tree and produces a blank dark screen. Validate first and render a
// clear configuration page instead.
type KeyStatus = "present" | "missing" | "malformed";

function classifyClerkKey(key: string | undefined): KeyStatus {
  if (!key || !key.trim()) return "missing";
  const trimmed = key.trim();
  // Real Clerk keys are pk_(test|live)_ followed by a long base64-ish blob
  // (with `$` used as the padding char). 16+ chars is well below any real key
  // length and well above placeholders like "replace_me".
  if (!/^pk_(test|live)_[A-Za-z0-9_\-+/=$]{16,}$/.test(trimmed)) {
    return "malformed";
  }
  return "present";
}

const keyStatus = classifyClerkKey(publishableKey);

// Diagnostics — print once on startup so it's visible in the browser console
// without having to add ad-hoc logs. We deliberately only show the first 12
// chars of the key so it's safe to share screenshots while debugging.
const detectedViteKeys = Object.keys(env)
  .filter((k) => k.startsWith("VITE_"))
  .sort();

if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.info(
    "[community-shield-os] startup",
    {
      mode: env.MODE,
      dev: env.DEV,
      prod: env.PROD,
      base_url: env.BASE_URL,
      has_VITE_CLERK_PUBLISHABLE_KEY: !!env.VITE_CLERK_PUBLISHABLE_KEY,
      has_VITE_CLERK_KEY: !!env.VITE_CLERK_KEY,
      publishable_key_status: keyStatus,
      publishable_key_prefix: publishableKey
        ? `${publishableKey.slice(0, 12)}…`
        : null,
      detected_VITE_env_keys: detectedViteKeys,
    },
  );
}
const rootEl = document.getElementById("root");

if (!rootEl) {
  // Hard failure: the static HTML is missing the mount point. Surface it
  // visibly rather than silently doing nothing.
  document.body.innerHTML =
    '<div style="color:#fff;background:#0b1220;padding:24px;font-family:system-ui">' +
    'Could not find <code>#root</code> in index.html. Did you change the template?' +
    "</div>";
  throw new Error("Missing #root element in index.html");
}

const root = ReactDOM.createRoot(rootEl);

if (keyStatus !== "present") {
  // eslint-disable-next-line no-console
  console.warn(
    `[community-shield-os] Clerk publishable key is ${keyStatus}. ` +
      "Showing configuration screen instead of mounting the app.",
  );
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <MissingClerkKey
          reason={keyStatus}
          rawKey={publishableKey}
          detectedViteKeys={detectedViteKeys}
          mode={env.MODE ?? "unknown"}
        />
      </ErrorBoundary>
    </React.StrictMode>,
  );
} else {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <ClerkProvider
          publishableKey={publishableKey as string}
          signInUrl="/sign-in"
          signUpUrl="/sign-up"
          signInFallbackRedirectUrl="/dashboard"
          signUpFallbackRedirectUrl="/dashboard"
        >
          <BrowserRouter>
            <App clerkKeyStatus={keyStatus} />
          </BrowserRouter>
        </ClerkProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

registerServiceWorker();
